// CategorySelect.tsx
import { useEffect, useState } from 'react';
import { TextField, MenuItem, CircularProgress } from '@mui/material';
import axios from 'axios';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';

type CategoryOption = {
  id: number;
  name: string;
};

type CategorySelectProps<FormValues> = {
  register: UseFormRegister<FormValues>;
  errors?: FieldErrors<FormValues>;
  name?: keyof FormValues; // default: 'category_id'
  label?: string;
  requiredMessage?: string;
  defaultValue?: number;
};

function CategorySelect<FormValues>({
  register,
  errors,
  name,
  label = 'Category',
  requiredMessage = 'Category is required',
  defaultValue,
}: CategorySelectProps<FormValues>) {
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem(TOKEN_KEY);

  const fieldName = (name || ('category_id' as keyof FormValues)) as
    | keyof FormValues
    | string;

  const fieldError = errors?.[fieldName as string] as any;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${url}/api/v1/admin/executive/categories`,
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : undefined,
          }
        );

        if (data?.success && Array.isArray(data.data)) {
          // adapt to your actual shape if needed
          setCategories(
            data.data.map((item: any) => ({
              id: item.id,
              name: item.name,
            }))
          );
        }
      } catch (err) {
        console.error('Failed to fetch executive categories', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [token]);

  return (
    <TextField
      select
      fullWidth
      margin='normal'
      id={String(fieldName)}
      label={label}
      error={!!fieldError}
      helperText={fieldError?.message}
      InputProps={{
        endAdornment: loading ? <CircularProgress size={18} /> : null,
      }}
      defaultValue={defaultValue}
      {...register(fieldName as any, {
        required: requiredMessage,
      })}
    >
      {categories.map((cat) => (
        <MenuItem key={cat.id} value={cat.id}>
          {cat.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

export default CategorySelect;
